import { DEV_MODE } from "Constant/app/process";
import moment from "moment";

/**
 * Dev only stand in for `trackers/reports/events` and the event-errors
 * endpoint, following `redux/slices/testEvents/testEvents.mock` and
 * `FailedLeadsModal.mock`: real data always wins, the fixtures only fill in
 * when the backend returned nothing so the reports page has something to draw.
 */

const MOCK_TRACKER_NAME = "SignalSight Demo Tracker";
/** Far enough back that every date-range option has room to move. */
const MOCK_TRACKER_CREATED_AT = "12-Feb-2026 09:24:11";

/**
 * Two high-volume destinations make it easy to verify that seven-digit
 * Y-axis labels fit while still exercising multiple chart legends.
 */
const MOCK_DESTINATIONS = [
    {
        name: "Meta CAPI",
        pixel_id: "1099238471123456",
        pdf_available: 1,
        baseVolume: 520000,
        destination_type: { code: "fbcapi", name: "Facebook Conversions API" },
    },
    {
        name: "TikTok Events",
        pixel_id: "8823001194567890",
        pdf_available: 0,
        baseVolume: 310000,
        destination_type: { code: "ttcapi", name: "TikTok Events API" },
    },
];

/**
 * One fixture per branch of the events table: high volume rows, a row that
 * errors heavily so the red error column shows, a clean row with zero errors,
 * and a long event name to exercise the cell truncation.
 */
const EVENT_FIXTURES = [
    { name: "PageView", weight: 1, errorRate: 0.01 },
    { name: "ViewContent", weight: 0.62, errorRate: 0.02 },
    { name: "Search", weight: 0.4, errorRate: 0.015 },
    { name: "AddToCart", weight: 0.28, errorRate: 0.06 },
    { name: "AddToWishlist", weight: 0.2, errorRate: 0.025 },
    { name: "InitiateCheckout", weight: 0.14, errorRate: 0.18 },
    { name: "Purchase", weight: 0.08, errorRate: 0 },
    { name: "Lead", weight: 0.12, errorRate: 0.04 },
    { name: "Contact", weight: 0.09, errorRate: 0.03 },
    { name: "Subscribe", weight: 0.06, errorRate: 0.05 },
    {
        name: "CompleteRegistrationFromLandingPageVariantB",
        weight: 0.05,
        errorRate: 0.09,
    },
];

/** Deterministic pseudo-random so a reload does not reshuffle the chart. */
const seededRatio = (seed) => {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
};

const buildDates = (dayCount, baseVolume, errorRate, seedOffset) => {
    const days = Math.max(1, dayCount);
    return Array.from({ length: days }, (_, index) => {
        const date = moment()
            .subtract(days - 1 - index, "days")
            .format("YYYY-MM-DD");
        // 0.65x - 1.35x of the base volume, with a mild weekly rise.
        const jitter = 0.65 + seededRatio(index + seedOffset) * 0.7;
        const trend = 1 + index / (days * 4);
        const allEvents = Math.round(baseVolume * jitter * trend);
        return {
            date,
            all_events: allEvents,
            all_errors: Math.round(allEvents * errorRate),
        };
    });
};

const sumBy = (dates, key) =>
    dates.reduce((total, entry) => total + (entry[key] || 0), 0);

const buildEventTypes = (dayCount, baseVolume, seedOffset) =>
    EVENT_FIXTURES.reduce((eventTypes, fixture, fixtureIndex) => {
        const dates = buildDates(
            dayCount,
            Math.max(4, Math.round(baseVolume * fixture.weight)),
            fixture.errorRate,
            seedOffset + fixtureIndex * 17,
        );

        eventTypes[fixture.name] = {
            dates,
            sum_data: {
                all_events: sumBy(dates, "all_events"),
                all_errors: sumBy(dates, "all_errors"),
            },
        };
        return eventTypes;
    }, {});

/** Rolls the per event series up into the pixel level `sum_data` the page reads. */
const buildPixelReport = (dayCount, baseVolume, seedOffset, destination) => {
    const eventTypes = buildEventTypes(dayCount, baseVolume, seedOffset);
    const eventNames = Object.keys(eventTypes);

    const dates = eventTypes[eventNames[0]].dates.map((entry, dateIndex) => ({
        date: entry.date,
        all_events: eventNames.reduce(
            (total, name) =>
                total + (eventTypes[name].dates[dateIndex]?.all_events || 0),
            0,
        ),
        all_errors: eventNames.reduce(
            (total, name) =>
                total + (eventTypes[name].dates[dateIndex]?.all_errors || 0),
            0,
        ),
    }));

    return {
        ...(destination
            ? {
                  destination_type_code: destination.destination_type.code,
                  destination_name: destination.name,
              }
            : {}),
        sum_data: {
            all_events: sumBy(dates, "all_events"),
            all_errors: sumBy(dates, "all_errors"),
            dates,
        },
        event_types: eventTypes,
    };
};

/** Merges the per pixel reports into the combined "All" bucket the chart splits by pixel. */
const buildAllReport = (pixelReports) => {
    const pixelKeys = Object.keys(pixelReports);
    const firstDates = pixelReports[pixelKeys[0]].sum_data.dates;

    const dates = firstDates.map((entry, dateIndex) => ({
        date: entry.date,
        all_events: pixelKeys.reduce(
            (total, key) =>
                total +
                (pixelReports[key].sum_data.dates[dateIndex]?.all_events || 0),
            0,
        ),
        all_errors: pixelKeys.reduce(
            (total, key) =>
                total +
                (pixelReports[key].sum_data.dates[dateIndex]?.all_errors || 0),
            0,
        ),
    }));

    const eventTypes = EVENT_FIXTURES.reduce((merged, fixture) => {
        const perPixel = pixelKeys.map(
            (key) => pixelReports[key].event_types[fixture.name],
        );
        const mergedDates = perPixel[0].dates.map((entry, dateIndex) => ({
            date: entry.date,
            all_events: perPixel.reduce(
                (total, series) =>
                    total + (series.dates[dateIndex]?.all_events || 0),
                0,
            ),
            all_errors: perPixel.reduce(
                (total, series) =>
                    total + (series.dates[dateIndex]?.all_errors || 0),
                0,
            ),
        }));

        merged[fixture.name] = {
            dates: mergedDates,
            sum_data: {
                all_events: sumBy(mergedDates, "all_events"),
                all_errors: sumBy(mergedDates, "all_errors"),
            },
        };
        return merged;
    }, {});

    return {
        sum_data: {
            all_events: sumBy(dates, "all_events"),
            all_errors: sumBy(dates, "all_errors"),
            dates,
        },
        event_types: eventTypes,
    };
};

/**
 * How many days of series to build. The page asks either by day count
 * (`days: 7`) or by an explicit range, so both are normalised here.
 */
const resolveDayCount = ({
    days,
    start_date: startDate,
    end_date: endDate,
}) => {
    if (startDate && endDate) {
        const span = moment(endDate).diff(moment(startDate), "days") + 1;
        return Math.min(90, Math.max(1, span));
    }
    const parsed = parseInt(days, 10);
    return Number.isFinite(parsed) && parsed > 0 ? Math.min(90, parsed) : 7;
};

export const buildDevMockTrackerReportsResponse = (payload = {}) => {
    const dayCount = resolveDayCount(payload);

    const pixelReports = MOCK_DESTINATIONS.reduce(
        (reports, destination, index) => {
            reports[destination.pixel_id] = buildPixelReport(
                dayCount,
                destination.baseVolume,
                index * 101 + 3,
                destination,
            );
            return reports;
        },
        {},
    );

    return {
        status: true,
        data: {
            reports: {
                All: buildAllReport(pixelReports),
                ...pixelReports,
            },
            tracker: {
                id: payload?.tracker_id ?? 1,
                tracker_name: MOCK_TRACKER_NAME,
                created_at: MOCK_TRACKER_CREATED_AT,
                sources: 3,
                destinations: MOCK_DESTINATIONS.map((destination, index) => ({
                    ...destination,
                    id: 41 + index,
                })),
            },
        },
    };
};

const ERROR_FIXTURES = [
    {
        event_name: "InitiateCheckout",
        title: "Invalid event_source_url",
        description:
            "The event_source_url parameter must be a fully qualified URL including the protocol.",
        message: "event_source_url: 'checkout' is not a valid absolute URL",
        error_count: 184,
    },
    {
        event_name: "Purchase",
        title: "Missing required parameter",
        description:
            "Purchase events require both a value and a currency parameter.",
        message: ["value is required", "currency is required"],
        error_count: 63,
    },
    {
        event_name: "AddToCart",
        title: "Unhashed user data",
        description:
            "Customer information parameters must be SHA-256 hashed before they are sent.",
        message: { em: "must be hashed", ph: "must be hashed" },
        error_count: 27,
    },
    {
        event_name: "CompleteRegistrationFromLandingPageVariantB",
        title: "Rate limited by destination",
        description:
            "The destination rejected part of the batch because the request rate exceeded its limit.",
        message: "Too many requests, retry after 60s",
        error_count: 9,
    },
];

export const buildDevMockEventErrors = () =>
    ERROR_FIXTURES.map((fixture, index) => ({
        ...fixture,
        pixel_id: MOCK_DESTINATIONS[index % MOCK_DESTINATIONS.length].pixel_id,
        created_at: moment()
            .subtract(index * 7 + 3, "hours")
            .format("YYYY-MM-DD HH:mm:ss"),
    }));

const isEmptyReportsResponse = (response) =>
    !response?.status ||
    !response?.data?.reports ||
    Object.keys(response.data.reports).length === 0;

/** Real data wins; the fixtures only stand in for an empty or failed response. */
export const withDevMockTrackerReports = (response, buildMock) => {
    if (!DEV_MODE) return response;
    return isEmptyReportsResponse(response) ? buildMock() : response;
};

export const withDevMockEventErrors = (errors, buildMock) => {
    if (!DEV_MODE) return errors;
    return Array.isArray(errors) && errors.length > 0 ? errors : buildMock();
};
